import React, { CSSProperties, Fragment, ReactElement, useRef } from "react";
import { wire } from "react-hot-wire";
import MaterialTable, { MTableToolbar, Query } from "material-table";
import { WithTranslation, withTranslation } from "react-i18next";

import IconEdit from "@material-ui/icons/Edit";
import IconDelete from "@material-ui/icons/Delete";
import IconCheckCircle from "@material-ui/icons/CheckCircle";
import IconError from "@material-ui/icons/Error";

import { PrizeService } from "services/prize/Prize.service";
import { DrawerService } from "services/drawer/Drawer.service";

import { Prize } from "models/prize/Prize";
import Table, { Column, ColumnDefinition } from "models/common/Table";

import Menu from "components/common/menu/Menu";
import Chip from "@material-ui/core/Chip";
import Avatar from "@material-ui/core/Avatar";

const status: {
  [status: string]: {
    label: string;
    icon: ReactElement;
    chipStyle: CSSProperties;
    avatarStyle: CSSProperties;
  };
} = {
  unavailable: {
    label: "Unavailable",
    icon: <IconError style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#de4b4b", color: "#FFF" },
    avatarStyle: { backgroundColor: "#903131" }
  },
  available: {
    label: "Available",
    icon: <IconCheckCircle style={{ color: "#FFF" }} />,
    chipStyle: { backgroundColor: "#4fba2c", color: "#FFF" },
    avatarStyle: { backgroundColor: "#39871f" }
  }
};

function PrizeList({
  prizeService,
  drawerService,
  t
}: {
  prizeService: PrizeService;
  drawerService: DrawerService;
} & WithTranslation) {
  const tableRef = useRef<any>(null);
  const refresh = () => tableRef.current.onQueryChange();

  return (
    <Fragment>
      <div className="m-3">
        <MaterialTable
          tableRef={tableRef}
          title={t("prize.prize.list.title")}
          options={{ ...Table.options, loadingType: "overlay" }}
          columns={[
            ...([
              {
                title: t("prize.prize.list.column.id"),
                field: "id",
                defaultSort: "desc"
              },
              {
                title: t("prize.prize.list.column.supplier"),
                field: "supplier",
                render: (prize: Prize) => (
                  <a
                    href="/"
                    onClick={event => {
                      event.preventDefault();

                      drawerService.activate("edit-prize-supplier", {
                        prizeSupplier: prize.supplier,
                        action: refresh
                      });
                    }}
                  >
                    {prize.supplier.type}
                  </a>
                )
              },
              {
                title: t("prize.prize.list.column.supplierItemId"),
                field: "supplierItemId"
              },
              {
                title: t("prize.prize.list.column.image"),
                field: "image",
                sorting: false,
                render: (prize: Prize) => (
                  <img src={prize.image} width="50px" alt="" />
                )
              },
              {
                title: t("prize.prize.list.column.name"),
                field: "name",
                cellStyle: { width: "100%" },
                customSort: (a, b) => a.name.localeCompare(b.name),
                render: (prize: Prize) => (
                  <a
                    href="/"
                    onClick={event => {
                      event.preventDefault();

                      drawerService.activate("edit-prize", {
                        prize,
                        action: refresh
                      });
                    }}
                  >
                    {prize.name}
                  </a>
                )
              },
              {
                title: t("prize.prize.list.column.nettoPrice"),
                field: "nettoPrice"
              },
              {
                title: t("prize.prize.list.column.priceMargin"),
                field: "priceMargin",
                render: prize =>
                  `${prize.priceMargin || prize.supplier.priceMargin}%`
              },
              {
                title: t("prize.prize.list.column.vatRate"),
                field: "vatRate",
                render: prize => `${prize.vatRate || 0}%`
              },
              {
                title: t("prize.prize.list.column.status"),
                field: "status",
                sorting: false,
                render: prize => (
                  <Chip
                    label={t(`prize.prize.list.status.${prize.status}`)}
                    clickable={true}
                    style={status[prize.status].chipStyle}
                    avatar={
                      <Avatar style={status[prize.status].avatarStyle}>
                        {status[prize.status].icon}
                      </Avatar>
                    }
                  />
                )
              },
              {
                title: t("prize.prize.list.column.actions"),
                sorting: false,
                editable: "never",
                render: prize =>
                  prize && (
                    <Menu
                      translationPath="prize.prize.list.rowMenu"
                      items={[
                        {
                          icon: <IconEdit fontSize="small" className="mr-2" />,
                          text: "edit",
                          onClick: () =>
                            drawerService.activate("edit-prize", {
                              prize,
                              action: refresh
                            })
                        },
                        {
                          icon: (
                            <IconDelete fontSize="small" className="mr-2" />
                          ),
                          text: "delete",
                          disabled: true
                        }
                      ]}
                    />
                  )
              }
            ] as ColumnDefinition<Prize>[]).map(
              column => new Column<Prize>(column)
            )
          ]}
          data={async (query: Query<Prize>) => await prizeService.list(query)}
          components={{
            Toolbar: props => (
              <MTableToolbar {...props} search={!props.selectedRows.length} />
            )
          }}
          actions={[
            {
              icon: "delete",
              tooltip: t("prize.prize.list.actions.delete.tooltip"),
              onClick: () =>
                window.confirm(
                  t("prize.prize.list.actions.delete.confirmation")
                )
            }
          ]}
          localization={t("prize.prize.list.localization", {
            returnObjects: true
          })}
        />
      </div>
    </Fragment>
  );
}

export default wire(
  ["prizeService", "drawerService"],
  withTranslation()(PrizeList)
);
